import React, { useContext } from "react";
import { isMobile, isIOS } from "react-device-detect";
import styles from "./ShareArrow.module.scss";
import { CopyToClipboard } from "react-copy-to-clipboard";
import CopiedContext from "../CopiedContext";
import ReactGA from "react-ga4";

const ShareArrow = ({ title, url, text, downloadUrl, isVideo = false, customBottom = "8px", sectionName, mediaShare, cityurl, venue, azureurl, videourl, guid, country }) => {
  const { showCopied, setShowCopied } = useContext(CopiedContext);
  
  // Build mediaShareURL like ShareBar does
  const mediaShareAddress = `https://${cityurl}/stories-shared-media`;
  let mediaShareURL = "";
  let shareText = "";

  const thumbnailParts = mediaShare?.thumbnail?.split("/");
  const filename = thumbnailParts?.[4];
  const thumbnailURL = filename?.split("_")[0];

  // Extract venue parameter for all URLs
  const venueParam = venue ? `&venue=${encodeURIComponent(venue)}` : "";

  // Use the text prop passed from the component, which already has the correct format
  shareText = text ? `${text} ` : "Check out this content from our Electric Shuffle visit. ";

  if (mediaShare?.mediaType === "PHOTO") {
    mediaShareURL = `${mediaShareAddress}?u=photo/${mediaShare.mediaURI.substring(0, mediaShare.mediaURI.lastIndexOf("."))}&t=i&section=group_photo${venueParam}`;
  } else if (mediaShare?.mediaType === "VIDEO" && sectionName === "Action Replay") {
    if (mediaShare?.videoURL) {
      // Extract asset code from Electric Shuffle blob storage URL
      // URL format: https://esstoriesvideostorage.blob.core.windows.net/asset-[guid]/[guid]_1920x1080.mp4
      const urlParts = mediaShare.videoURL.split('/');
      const assetContainer = urlParts[urlParts.length - 2]; // asset-[guid]
      const filename = urlParts[urlParts.length - 1]; // [guid]_1920x1080.mp4
      const assetPath = `${assetContainer}/${filename}`;
      
      const vidType = "vidblob";
      mediaShareURL = `${mediaShareAddress}?u=${vidType}/${assetPath}&t=v&p=a&utm_source=Stories&utm_medium=link_share&utm_campaign=action_replay`;
    }
  } else if (mediaShare?.mediaType === "VIDEO" && sectionName === "Loop Cams") {
    if (mediaShare?.videoURL) {
      // Extract asset code from Electric Shuffle blob storage URL
      const urlParts = mediaShare.videoURL.split('/');
      const assetContainer = urlParts[urlParts.length - 2]; // asset-[guid]
      const filename = urlParts[urlParts.length - 1]; // [guid]_1920x1080.mp4
      const assetPath = `${assetContainer}/${filename}`;
      
      const vidType = "vidblob";
      mediaShareURL = `${mediaShareAddress}?u=${vidType}/${assetPath}&t=v&p=a&utm_source=Stories&utm_medium=link_share&utm_campaign=loop_cam`;
    }
  }

  // For US, only override if we don't already have a specific media share URL
  if (country === "us" && !mediaShareURL) {
    mediaShareURL = `https://stories.us.electricshuffle.com/${guid}`;
  }

  let trackEvent = (category, action, label = "") => {
    ReactGA.event({
      category: category,
      action: action,
      label: label,
    });
  };

  const handleShare = async (event) => {
    // Prevent the click from bubbling up to parent elements
    event.stopPropagation()

    trackEvent("Share", "Native Share " + sectionName, "Mobile Share Button");

    // Create the native share text with current story URL
    const currentUrl = window.location.href;
    const fullShareText = `${shareText}See the full Story here: ${currentUrl}?utm_source=Stories&utm_medium=native_share&utm_campaign=native`;

    // Check if native share is supported
    if (!navigator.share) {
      // console.log("Native share not supported");
      // Fallback to copy URL
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(fullShareText);
          // console.log("URL copied to clipboard");
          setShowCopied(true);
        } catch (error) {
          // console.log("Failed to copy URL:", error);
        }
      }
      return;
    }

    // iOS: Try to fetch and share the actual file (full functionality)
    if (isIOS && downloadUrl) {
      try {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        const fileName = isVideo ? "electric-shuffle-video.mp4" : "electric-shuffle-photo.jpg";
        const file = new File([blob], fileName, { type: blob.type });

        // Check if we can share files
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: title || "Electric Shuffle Stories",
            text: fullShareText,
            files: [file],
          });
          return;
        }
      } catch (error) {
        // console.log("Error sharing file on iOS:", error);
      }
    }

    // Fallback: Share URL and text (works on both iOS and Android)
    try {
      const shareData = {
        title: title || "Electric Shuffle Stories",
        text: fullShareText,
      };
      
      // Add URL separately for iOS (better support)
      if (isIOS) {
        shareData.url = currentUrl;
      }
      
      await navigator.share(shareData);
    } catch (error) {
      // console.log("Error sharing:", error);
      // Final fallback to copy URL if share fails
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(fullShareText);
          // console.log("Share failed, URL copied to clipboard instead");
          setShowCopied(true);
        } catch (clipboardError) {
          // console.log("Failed to copy URL:", clipboardError);
        }
      }
    }
  };

  const handleLinkCopy = () => {
    trackEvent("Share", "Share " + sectionName, "Link");
    setShowCopied(true);
  };

  return (
    <div 
      className={styles.shareContainer} 
      style={{ bottom: customBottom }}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
    >
      {/* Link Copy Button */}
      <CopyToClipboard text={mediaShareURL} onCopy={handleLinkCopy}>
        <button 
          className={styles.shareIcon} 
          onClick={(e) => e.stopPropagation()} 
          aria-label="Copy Link"
        >
          <svg width="20px" height="20px" strokeWidth="1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000">
            <path d="M14 11.9976C14 9.5059 11.683 7 8.85714 7C8.52241 7 7.41904 7.00001 7.14286 7.00001C4.30254 7.00001 2 9.23752 2 11.9976C2 14.376 3.70973 16.3664 6 16.8714C6.36756 16.9525 6.75006 16.9952 7.14286 16.9952" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M10 11.9976C10 14.4893 12.317 16.9952 15.1429 16.9952C15.4776 16.9952 16.581 16.9952 16.8571 16.9952C19.6975 16.9952 22 14.7577 22 11.9976C22 9.6192 20.2903 7.62884 18 7.12383C17.6324 7.04278 17.2499 6.99999 16.8571 6.99999" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
        </button>
      </CopyToClipboard>

      {/* Share Button - Only show on mobile */}
      {isMobile && (
        <button className={styles.shareIcon} onClick={handleShare} aria-label="Share">
          <svg width="20px" height="20px" strokeWidth="1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000">
            <path d="M20 13V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V13" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M12 15V3M12 3L8.5 6.5M12 3L15.5 6.5" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
        </button>
      )}
    </div>
  );
};

export default ShareArrow;